import { useCallback, useEffect, useMemo, useState } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';

import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { approveSignup, ApproveSignupPayload } from '@/api/signups';
import { useGetSignupByEmail } from '@/hooks/api/use-get-signup-by-email';
import { useGetClinics } from '@/hooks/api/use-get-clinics';
import { useCountriesOptions } from '@/hooks/api/use-countries-options';
import { toast_success } from '@/libs/toast-wrappers';
import { ROLES_LABELS } from '@/utils/constants';
import { phoneSchema } from '@/utils/zod-schemas';

import { InputField } from '@/components/form/input-field';
import { SelectField } from '@/components/form/select-field';
import { Button } from '@/components/ui/button';
import { FetchStates } from '@/components/fetch-states';

const createSchema = (t: TFunction) =>
  z.object({
    user_email: z.string(),
    name: z.string().trim().min(1, t('input-required')),
    phone: phoneSchema,
    country: z.string().trim().min(1, t('input-required')),
    city: z.string().trim().min(1, t('input-required')),
    provinceState: z.string().trim().min(1, t('input-required')),
    requestedRole: z.string().trim().min(1, t('input-required')),
    clinic_id: z.string().trim().min(1, t('admin-users-clinic-required')),
    postal: z.string().trim().optional(),
    address: z.string().trim().optional(),
  });

type ApprovalFormData = z.infer<ReturnType<typeof createSchema>>;

export function ApprovalForm() {
  const { userId } = useParams(); // userId is actually the user_email
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { signup, loading, error, refetch } = useGetSignupByEmail(userId);
  const { clinics, loading: clinicsLoading } = useGetClinics();
  const { COUNTRIES, isLoading: countriesLoading } = useCountriesOptions();

  const { onFormClose } = useOutletContext<{
    onFormClose: () => Promise<void>;
  }>();

  const schema = useMemo(() => createSchema(t), [t]);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<ApprovalFormData>({ resolver: zodResolver(schema) });

  // Transform clinics data to SelectField format: [{ clinic_id: clinic_name }]
  const clinicOptions =
    clinics?.map((clinic) => ({
      [clinic.clinic_id ?? '']: clinic.clinic_name ?? 'Unknown Clinic',
    })) ?? [];

  // Fill form with signup data
  const fillForm = useCallback(() => {
    if (!signup) return;

    setValue('user_email', signup.user_email);
    setValue('name', signup.name);
    setValue('phone', signup.phone);
    setValue('country', signup.country);
    setValue('city', signup.city);
    setValue('provinceState', signup.provinceState);
    setValue('requestedRole', signup.requestedRole);
    setValue('postal', signup.postal ?? '');
    setValue('address', signup.address ?? '');
    // clinic_id should be selected by admin, so don't pre-fill
  }, [signup, setValue]);

  useEffect(() => {
    fillForm();
  }, [fillForm]);

  const onSubmitApproval: SubmitHandler<ApprovalFormData> = async (data) => {
    setIsSubmitting(true);
    try {
      const payload: ApproveSignupPayload = {
        user_email: data.user_email,
        name: data.name,
        phone: data.phone,
        country: data.country,
        city: data.city,
        provinceState: data.provinceState,
        requestedRole: data.requestedRole,
        clinic_id: data.clinic_id,
      };

      // Add optional fields if present
      if (data.postal) payload.postal = data.postal;
      if (data.address) payload.address = data.address;

      await approveSignup(payload);
      toast_success(t('admin-users-approval-success'));
      await onFormClose();
    } catch (error) {
      console.error('Error approving signup:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <FetchStates
      loading={loading}
      error={error?.message}
      refetch={() => void refetch()}
      empty={!signup}
    >
      <form
        onSubmit={handleSubmit(onSubmitApproval)}
        className="w-full my-6 mx-4 flex flex-col gap-6"
      >
        <div className="w-full grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Name */}
          <InputField
            label={t('signup-full-name')}
            required
            autoComplete="name"
            classNameFieldSet="lg:col-span-2"
            type="text"
            disabled={isSubmitting || clinicsLoading}
            errorMessage={errors.name?.message}
            {...register('name')}
          />

          {/* Email - Disabled */}
          <InputField label={t('login-email')} type="email" disabled {...register('user_email')} />

          {/* Phone */}
          <InputField
            label={t('signup-phone')}
            required
            autoComplete="tel"
            type="tel"
            disabled={isSubmitting || clinicsLoading}
            errorMessage={errors.phone?.message}
            {...register('phone')}
          />

          {/* Original Clinic Name (read-only reference) */}
          {signup?.clinic && (
            <InputField
              label={t('admin-users-original-clinic-label')}
              type="text"
              disabled
              value={signup.clinic}
              name="original_clinic"
              help={t('admin-users-clinic-helper').replace('{clinic}', signup.clinic)}
            />
          )}

          {/* Clinic Selection */}
          <SelectField
            label={t('admin-users-select-clinic')}
            options={clinicOptions}
            enableEmptyOption
            disabled={isSubmitting || clinicsLoading}
            errorMessage={errors.clinic_id?.message}
            {...register('clinic_id')}
          />

          {/* Requested Role */}
          <SelectField
            label={t('signup-role')}
            required
            options={ROLES_LABELS}
            enableEmptyOption
            disabled={isSubmitting || clinicsLoading}
            errorMessage={errors.requestedRole?.message}
            {...register('requestedRole')}
          />

          {/* Address - Optional */}
          <InputField
            label={t('signup-address')}
            autoComplete="street-address"
            classNameFieldSet="lg:col-span-2"
            type="text"
            disabled={isSubmitting || clinicsLoading}
            {...register('address')}
          />

          {/* City */}
          <InputField
            label={t('signup-city')}
            autoComplete="address-level2"
            type="text"
            required
            disabled={isSubmitting || clinicsLoading}
            errorMessage={errors.city?.message}
            {...register('city')}
          />

          {/* Province/State */}
          <InputField
            label={t('signup-state')}
            autoComplete="address-level1"
            type="text"
            required
            disabled={isSubmitting || clinicsLoading}
            errorMessage={errors.provinceState?.message}
            {...register('provinceState')}
          />

          {/* Postal Code - Optional */}
          <InputField
            label={t('signup-postal')}
            autoComplete="postal-code"
            type="text"
            disabled={isSubmitting || clinicsLoading}
            {...register('postal')}
          />

          {/* Country */}
          <SelectField
            label={t('signup-country')}
            required
            options={COUNTRIES ?? []}
            enableEmptyOption
            autoComplete="country"
            disabled={isSubmitting || clinicsLoading || countriesLoading}
            errorMessage={errors.country?.message}
            {...register('country')}
          />
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap items-center gap-4">
          <Button type="submit" loading={isSubmitting} disabled={clinicsLoading} className="button">
            {t('admin-users-approve-button')}
          </Button>
        </div>
      </form>
    </FetchStates>
  );
}
